/**
 * Phase 83 — Trial Reports list & creation wizard
 * ================================================
 *
 * Renders a single page that:
 *   • lists every Trial Report visible to the user (Admin/Staff: all
 *     accessible; Player/Parent: finalised only)
 *   • surfaces a "New Trial Report" wizard for Admin/Staff
 *
 * Trial Reports are INDEPENDENT from Season Reports — both can exist
 * for the same athlete in parallel. The wizard creates a draft via
 * POST /api/trial-reports and then navigates to the editor route
 * /trial-reports/edit/:id where the 14-section narrative authoring
 * happens.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, ChevronRight, Search, Archive, CheckCircle2, FileEdit } from 'lucide-react';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  draft:     'bg-amber-100 text-amber-700 border-amber-200',
  finalized: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  archived:  'bg-gray-100 text-gray-500 border-gray-200',
};

const StatusBadge = ({ status }) => (
  <Badge className={`text-[10px] uppercase tracking-wide border ${STATUS_TONE[status] || ''}`}>
    {status || 'draft'}
  </Badge>
);

export default function TrialReportsList() {
  const { user, hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  // Phase 83 — AuthContext registers a global axios Authorization header
  // (see AuthContext.js useEffect on `token`). We deliberately do NOT
  // re-read `token` here because it isn't part of the exported value
  // object; relying on axios defaults keeps this page in lock-step
  // with the rest of the app.
  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';
  // Phase 87 — creation is now gated by the trial_reports EDIT permission
  // (not merely the Staff role). Admin passes automatically via hasModuleAccess.
  const canCreate = hasModuleAccess(PM.TRIAL_REPORTS, 'edit');

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showWizard, setShowWizard] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/api/trial-reports?${params}`);
      setReports(res.data || []);
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error(err.response?.data?.detail || 'Failed to load Trial Reports');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const visible = reports.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.athlete_name || '').toLowerCase().includes(q) ||
      (r.title || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6" data-testid="trial-reports-list-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FileText size={22} className="text-pfap-green" />
              Trial Reports
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Lightweight evaluation reports for players on trial. Independent from Season Reports — both can coexist for the same athlete.
            </p>
          </div>
          {canCreate && (
            <Button
              className="bg-pfap-green hover:bg-pfap-green/90 text-white"
              onClick={() => setShowWizard(true)}
              data-testid="trial-reports-new-btn"
            >
              <Plus size={14} className="mr-1.5" />
              New Trial Report
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2" data-testid="trial-reports-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by athlete or title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="trial-reports-search"
            />
          </div>
          {[
            { key: 'all',       label: 'All' },
            { key: 'draft',     label: 'Drafts',     icon: FileEdit },
            { key: 'finalized', label: 'Finalised',  icon: CheckCircle2 },
            { key: 'archived',  label: 'Archived',   icon: Archive },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === t.key
                  ? 'bg-pfap-green text-white border-pfap-green'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
              }`}
              data-testid={`trial-reports-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <p className="text-sm text-gray-500" data-testid="trial-reports-loading">Loading Trial Reports…</p>
        ) : visible.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center" data-testid="trial-reports-empty">
              <FileText size={28} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No Trial Reports yet.</p>
              {isStaff && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Click <span className="font-semibold">New Trial Report</span> to create one.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {visible.map(r => (
              <Card
                key={r.id}
                className="cursor-pointer hover:border-pfap-green/40 transition-colors"
                onClick={() => navigate(`/trial-reports/${r.id}`)}
                data-testid={`trial-report-card-${r.id}`}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {r.title}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {r.athlete_name} · {r.squad || r.athlete_squad || '—'}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                        {r.trial_start_date} → {r.trial_end_date}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <StatusBadge status={r.status} />
                      <ChevronRight size={14} className="text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {showWizard && (
          <NewTrialReportWizard
            onClose={() => setShowWizard(false)}
            onCreated={(id) => {
              setShowWizard(false);
              navigate(`/trial-reports/${id}`);
            }}
          />
        )}
      </div>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// New Trial Report Wizard
// ─────────────────────────────────────────────────────────────────────
const NewTrialReportWizard = ({ onClose, onCreated }) => {
  const [athletes, setAthletes] = useState([]);
  const [form, setForm] = useState({
    athlete_id: '',
    title: '',
    trial_start_date: new Date().toISOString().slice(0, 10),
    trial_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    squad: '',
    position: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/athletes?season_scope=all`);
        setAthletes(res.data || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const submit = async () => {
    if (!form.athlete_id || !form.title.trim()) {
      toast.error('Pick an athlete and give the report a title.');
      return;
    }
    if (form.trial_end_date < form.trial_start_date) {
      toast.error('End date must be on or after start date.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/trial-reports`, form);
      toast.success('Trial Report draft created.');
      onCreated(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create Trial Report.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="trial-report-wizard"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">New Trial Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="block text-xs">
            <span className="text-gray-600 dark:text-gray-300 font-medium">Athlete</span>
            <div className="mt-1">
              <SearchableAthleteSelect
                athletes={athletes}
                value={form.athlete_id}
                onChange={(id) => {
                  const a = athletes.find(x => x.id === id);
                  setForm(f => ({
                    ...f,
                    athlete_id: id,
                    squad: f.squad || a?.squad || '',
                    position: f.position || a?.position || '',
                  }));
                }}
                placeholder="Search by athlete name, squad or position…"
                data-testid="wizard-athlete-select"
              />
            </div>
          </label>

          <label className="block text-xs">
            <span className="text-gray-600 dark:text-gray-300 font-medium">Title</span>
            <Input
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Trial — June Week 1"
              className="mt-1 h-9 text-sm"
              data-testid="wizard-title-input"
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs">
              <span className="text-gray-600 dark:text-gray-300 font-medium">Trial start</span>
              <Input
                type="date"
                value={form.trial_start_date}
                onChange={(e) => setForm(f => ({ ...f, trial_start_date: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="wizard-start-date"
              />
            </label>
            <label className="block text-xs">
              <span className="text-gray-600 dark:text-gray-300 font-medium">Trial end</span>
              <Input
                type="date"
                value={form.trial_end_date}
                onChange={(e) => setForm(f => ({ ...f, trial_end_date: e.target.value }))}
                className="mt-1 h-9 text-sm"
                data-testid="wizard-end-date"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs">
              <span className="text-gray-600 dark:text-gray-300 font-medium">Squad trained with</span>
              <Input
                value={form.squad}
                onChange={(e) => setForm(f => ({ ...f, squad: e.target.value }))}
                placeholder="U15 · U17 · …"
                className="mt-1 h-9 text-sm"
                data-testid="wizard-squad-input"
              />
            </label>
            <label className="block text-xs">
              <span className="text-gray-600 dark:text-gray-300 font-medium">Position</span>
              <Input
                value={form.position}
                onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))}
                placeholder="Midfielder, Defender, …"
                className="mt-1 h-9 text-sm"
                data-testid="wizard-position-input"
              />
            </label>
          </div>

          <p className="text-[10px] text-gray-400 mt-2">
            Attendance, matches and other observable data will be pre-filled from records inside the trial window.
            You&apos;ll add the coach narrative on the next screen.
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="wizard-cancel">Cancel</Button>
            <Button
              className="bg-pfap-green hover:bg-pfap-green/90 text-white"
              onClick={submit}
              disabled={submitting}
              data-testid="wizard-create"
            >
              {submitting ? 'Creating…' : 'Create draft'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
